import { css, cva } from '@styled-system/css';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '@/shared/hooks/useIsMobile';

const ZOOM_SCALE = 2.2;

interface ImageLightboxProps {
  images: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  alt?: string;
}

// ─── Styles ─────────────────────────────────────────────────────────────

const backdrop = css({
  position: 'fixed',
  inset: 0,
  zIndex: 1200,
  bg: 'rgba(0, 0, 0, 0.92)',
  display: 'flex',
  flexDirection: 'column',
  animation: 'fadeIn 160ms ease-out',
});

const header = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
  paddingBottom: '3',
  paddingX: '5',
  color: 'rgba(255, 255, 255, 0.85)',
  fontFamily: 'sans',
  flexShrink: 0,
});

const counter = css({
  fontSize: 'bodySm',
  fontWeight: 'medium',
  fontFamily: 'mono',
  letterSpacing: '0.5px',
});

const closeBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: 'full',
  background: 'rgba(255, 255, 255, 0.1)',
  border: 'none',
  color: 'white',
  cursor: 'pointer',
  transition: 'background 120ms ease',
  _hover: { background: 'rgba(255, 255, 255, 0.2)' },
});

const stage = css({
  flex: 1,
  minHeight: 0,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingX: '4',
  // Clip l'image zoomée (loupe) pour qu'elle ne déborde pas sur header / dots.
  overflow: 'hidden',
});

const imgEl = css({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  userSelect: 'none',
  // L'image se renouvelle quand l'index change → discrete fade-in.
  animation: 'fadeIn 200ms ease-out',
  // Effet loupe au hover (desktop) : transform piloté en inline style.
  transition: 'transform 180ms ease-out',
  willChange: 'transform',
});

const imgZoomable = css({ cursor: 'zoom-in' });

const navBtn = cva({
  base: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: 'full',
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 120ms ease, opacity 120ms ease',
    _hover: { background: 'rgba(255, 255, 255, 0.25)' },
    _disabled: { opacity: 0.3, cursor: 'not-allowed' },
  },
  variants: {
    side: {
      left: { left: '12px' },
      right: { right: '12px' },
    },
  },
});

const dotsRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2',
  paddingY: '4',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  flexShrink: 0,
});

const dot = cva({
  base: {
    width: '8px',
    height: '8px',
    borderRadius: 'full',
    background: 'rgba(255, 255, 255, 0.3)',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'background 120ms ease, transform 120ms ease',
    _hover: { background: 'rgba(255, 255, 255, 0.5)' },
  },
  variants: {
    active: {
      true: { background: 'white', transform: 'scale(1.3)' },
      false: {},
    },
  },
});

// ─── Component ──────────────────────────────────────────────────────────

export const ImageLightbox: FC<ImageLightboxProps> = ({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
  alt,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [index, setIndex] = useState(initialIndex);
  // Effet loupe (desktop) : origine du zoom = position % du curseur sur l'image.
  const [zoomOrigin, setZoomOrigin] = useState<{ x: number; y: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // Rect de l'image NON zoomée, capturé pour un % stable (pas de clignotement :
  // les handlers sont sur le `stage` fixe, pas sur l'image qui scale).
  const imgRectRef = useRef<DOMRect | null>(null);
  // Snapshot des props à l'ouverture. Le feed peut refetch / l'item peut
  // changer de position pendant que la lightbox est ouverte (nouveaux items
  // poussés en haut du flux) → on pin les `images` pour que l'image affichée
  // ne saute pas. Reset à la fermeture pour repartir frais à la prochaine
  // ouverture.
  const [pinnedImages, setPinnedImages] = useState<string[]>(images);
  const [pinnedAlt, setPinnedAlt] = useState<string | undefined>(alt);

  useEffect(() => {
    if (isOpen) {
      setPinnedImages(images);
      setPinnedAlt(alt);
      setIndex(initialIndex);
    }
    // Volontairement on ne dépend que de `isOpen` : on snapshot une seule
    // fois à l'ouverture et on ignore les updates de props pendant l'affichage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Body scroll lock + restore on close/unmount.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : pinnedImages.length - 1));
  }, [pinnedImages.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i < pinnedImages.length - 1 ? i + 1 : 0));
  }, [pinnedImages.length]);

  // Keyboard nav.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, goPrev, goNext]);

  // Reset du zoom au changement d'image (l'img re-monte via `key`).
  useEffect(() => {
    setZoomOrigin(null);
    imgRectRef.current = null;
  }, [index]);

  if (!isOpen || pinnedImages.length === 0) return null;

  const safeIndex = Math.min(Math.max(0, index), pinnedImages.length - 1);
  const currentSrc = pinnedImages[safeIndex]!;
  const hasMultiple = pinnedImages.length > 1;

  // Loupe au hover (desktop only). Handlers posés sur le `stage` (taille fixe) :
  // l'image scale à l'intérieur sans changer la zone de hit → aucun clignotement.
  // On capture le rect de l'image NON zoomée pour mapper le curseur en %.
  const captureImgRect = () => {
    if (!zoomOrigin && imgRef.current) imgRectRef.current = imgRef.current.getBoundingClientRect();
  };
  const onZoomMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRectRef.current) captureImgRect();
    const rect = imgRectRef.current;
    if (!rect) return;
    const inside =
      e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (!inside) {
      if (zoomOrigin) setZoomOrigin(null); // curseur hors de l'image → on dézoome
      return;
    }
    setZoomOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };
  const onZoomLeave = () => {
    setZoomOrigin(null);
    imgRectRef.current = null;
  };
  const zoomHandlers = isMobile ? {} : { onMouseEnter: captureImgRect, onMouseMove: onZoomMove, onMouseLeave: onZoomLeave };

  // Click on backdrop closes. CRITICAL : React events portaled to body still
  // bubble through the React tree (parent = the `<article>` of ItemFeed) → if
  // we don't stopPropagation here, every click inside the lightbox (close,
  // dots, image, navigation buttons…) re-triggers the card's onClick and
  // re-opens the item drawer right after the lightbox closes.
  const onBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className={backdrop}
      onClick={onBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={pinnedAlt ? t('ui.lightbox.imageAlt', { alt: pinnedAlt }) : t('ui.lightbox.fullscreenImage')}
    >
      <div className={header}>
        <span className={counter}>
          {hasMultiple ? `${safeIndex + 1} / ${pinnedImages.length}` : ''}
        </span>
        <button
          type="button"
          className={closeBtn}
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>

      <div className={stage} onClick={onBackdropClick} {...zoomHandlers}>
        {/* `key` force le re-mount → l'animation `fadeIn` rejoue à chaque image. */}
        <img
          key={safeIndex}
          ref={imgRef}
          src={currentSrc}
          alt={pinnedAlt ?? ''}
          className={`${imgEl} ${isMobile ? '' : imgZoomable}`}
          draggable={false}
          onClick={(e) => e.stopPropagation()}
          style={
            zoomOrigin
              ? { transform: `scale(${ZOOM_SCALE})`, transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%` }
              : undefined
          }
        />
        {hasMultiple && (
          <>
            <button
              type="button"
              className={navBtn({ side: 'left' })}
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              aria-label={t('ui.lightbox.previousImage')}
            >
              <ChevronLeft size={24} strokeWidth={2} />
            </button>
            <button
              type="button"
              className={navBtn({ side: 'right' })}
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              aria-label={t('ui.lightbox.nextImage')}
            >
              <ChevronRight size={24} strokeWidth={2} />
            </button>
          </>
        )}
      </div>

      {hasMultiple && (
        <div className={dotsRow}>
          {pinnedImages.map((_, i) => (
            <button
              key={i}
              type="button"
              className={dot({ active: i === safeIndex })}
              onClick={() => setIndex(i)}
              aria-label={t('ui.lightbox.goToImage', { n: i + 1 })}
              aria-current={i === safeIndex ? 'true' : undefined}
            />
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
};
